import { useState, useEffect } from 'react';
import {
    RefreshCw,
    Check,
    X,
    Cpu,
    Trash2,
    Image,
} from 'lucide-react';
import { adminApi } from '../../lib/api';
import type { ActiveModel } from '../../lib/types';
import { useI18n } from '../../i18n';

export default function AdminModels() {
    const { t } = useI18n();
    const [models, setModels] = useState<ActiveModel[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isFetching, setIsFetching] = useState(false);

    useEffect(() => {
        loadModels();
    }, []);

    const loadModels = async () => {
        try {
            const response = await adminApi.getModels();
            if (response.success) {
                setModels(response.models);
            }
        } catch (error) {
            console.error('Failed to load models:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch models from Google
    const handleFetchModels = async () => {
        setIsFetching(true);
        try {
            const response = await adminApi.fetchModels();
            if (response.success) {
                alert(`${t.admin.models.modelsFetched}: ${response.added} added, ${response.updated} updated`);
                loadModels();
            }
        } catch (error) {
            console.error('Failed to fetch models:', error);
            alert(t.admin.models.fetchFailed);
        } finally {
            setIsFetching(false);
        }
    };

    // Toggle model active status
    const handleToggleActive = async (model: ActiveModel) => {
        try {
            const response = await adminApi.updateModel(model.id, {
                is_active: !model.is_active,
            });
            if (response.success && response.model) {
                setModels((prev) =>
                    prev.map((m) => (m.id === model.id ? response.model : m))
                );
            }
        } catch (error) {
            console.error('Failed to update model:', error);
        }
    };

    // Toggle image model status
    const handleToggleImageModel = async (model: ActiveModel) => {
        try {
            const response = await adminApi.updateModel(model.id, {
                is_image_model: !model.is_image_model,
            });
            if (response.success && response.model) {
                setModels((prev) =>
                    prev.map((m) => (m.id === model.id ? response.model : m))
                );
            }
        } catch (error) {
            console.error('Failed to update model:', error);
        }
    };

    // Delete model
    const handleDeleteModel = async (model: ActiveModel) => {
        if (!confirm(`"${model.display_name || model.model_id}" ${t.admin.models.deleteConfirm}`)) return;

        try {
            await adminApi.deleteModel(model.id);
            setModels((prev) => prev.filter((m) => m.id !== model.id));
        } catch (error) {
            console.error('Failed to delete model:', error);
        }
    };

    const activeCount = models.filter((m) => m.is_active).length;

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-dark-100">{t.admin.models.title}</h1>
                    <p className="text-dark-400 mt-1">
                        {t.admin.models.description} ({activeCount} {t.admin.models.active.toLowerCase()})
                    </p>
                </div>
                <button
                    onClick={handleFetchModels}
                    disabled={isFetching}
                    className="btn-primary"
                >
                    <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
                    {t.admin.models.fetchFromGoogle}
                </button>
            </div>

            {isLoading ? (
                <div className="flex items-center justify-center py-20">
                    <div className="spinner text-accent-500 w-8 h-8" />
                </div>
            ) : models.length === 0 ? (
                <div className="card text-center py-12">
                    <Cpu className="w-12 h-12 text-dark-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-dark-300 mb-2">
                        {t.admin.models.noModels}
                    </h3>
                    <p className="text-dark-500 mb-4">
                        {t.admin.models.noModelsDesc}
                    </p>
                    <button
                        onClick={handleFetchModels}
                        disabled={isFetching}
                        className="btn-primary mx-auto"
                    >
                        <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
                        {t.admin.models.fetchModels}
                    </button>
                </div>
            ) : (
                <div className="grid gap-3">
                    {models.map((model) => (
                        <div
                            key={model.id}
                            className={`card flex items-center justify-between ${model.is_active ? 'border-accent-500/30' : 'opacity-60'
                                }`}
                        >
                            <div className="flex items-center gap-4">
                                <div
                                    className={`w-10 h-10 rounded-lg flex items-center justify-center ${model.is_active
                                        ? 'bg-accent-500/10 text-accent-400'
                                        : 'bg-dark-800 text-dark-500'
                                        }`}
                                >
                                    <Cpu className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="font-medium text-dark-100">
                                        {model.display_name || model.model_id}
                                    </h3>
                                    <p className="text-sm text-dark-500">{model.model_id}</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                {/* Image model toggle */}
                                <button
                                    onClick={() => handleToggleImageModel(model)}
                                    className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${model.is_image_model
                                        ? 'bg-purple-500/10 text-purple-400 hover:bg-purple-500/20'
                                        : 'bg-dark-800 text-dark-500 hover:bg-dark-700'
                                        }`}
                                    title="이미지 모델"
                                >
                                    <Image className="w-3.5 h-3.5" />
                                    IMG
                                </button>

                                {/* Active toggle */}
                                <button
                                    onClick={() => handleToggleActive(model)}
                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${model.is_active
                                        ? 'bg-green-500/10 text-green-400 hover:bg-green-500/20'
                                        : 'bg-dark-800 text-dark-400 hover:bg-dark-700'
                                        }`}
                                >
                                    {model.is_active ? (
                                        <>
                                            <Check className="w-4 h-4" />
                                            {t.admin.models.active}
                                        </>
                                    ) : (
                                        <>
                                            <X className="w-4 h-4" />
                                            {t.admin.models.inactive}
                                        </>
                                    )}
                                </button>

                                {/* Delete */}
                                <button
                                    onClick={() => handleDeleteModel(model)}
                                    className="btn-icon text-red-400"
                                    title={t.common.delete}
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Help text */}
            <div className="mt-6 p-4 bg-dark-800/50 rounded-lg border border-dark-800">
                <h4 className="font-medium text-dark-200 mb-2">{t.admin.models.helpTitle}</h4>
                <ul className="text-sm text-dark-400 space-y-1">
                    {t.admin.models.helpItems.map((item, index) => (
                        <li key={index}>• {item}</li>
                    ))}
                </ul>
            </div>
        </div>
    );
}
